import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";
import { services as staticServices } from "../content/services";

const TABLE_NAME = "services";

const ensureArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }
    if (value === null || value === undefined) {
        return [];
    }
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            return value ? [value] : [];
        }
    }
    return [];
};

const mapRecord = (record) => ({
    id: record.id,
    slug: record.slug,
    category: typeof record.category === "string" && record.category.trim() ? record.category.trim() : "general",
    cardTitle: record.card_title ?? record.cardTitle ?? record.title,
    cardSummary: record.card_summary ?? record.cardSummary ?? "",
    title: record.title,
    intro: record.intro ?? "",
    focusPoints: ensureArray(record.focus_points ?? record.focusPoints),
    highlights: ensureArray(record.highlights),
    deliverables: ensureArray(record.deliverables),
    process: ensureArray(record.process),
    tools: ensureArray(record.tools),
    faq: Array.isArray(record.faq) ? record.faq : ensureArray(record.faq),
    ctaLabel: record.cta_label ?? record.ctaLabel ?? "Teklif al",
    ctaUrl: record.cta_url ?? record.ctaUrl ?? "mailto:studio@modelerman.com",
    orderIndex: record.order_index ?? record.orderIndex ?? 0,
    published: record.published ?? true,
});

const toSupabaseRecord = (record, index = 0) => ({
    id: record.id,
    slug: record.slug,
    category: record.category ?? "general",
    card_title: record.cardTitle ?? record.title,
    card_summary: record.cardSummary ?? "",
    title: record.title,
    intro: record.intro ?? "",
    focus_points: record.focusPoints ?? [],
    highlights: record.highlights ?? [],
    deliverables: record.deliverables ?? [],
    process: record.process ?? [],
    tools: record.tools ?? [],
    faq: record.faq ?? [],
    cta_label: record.ctaLabel ?? "Teklif al",
    cta_url: record.ctaUrl ?? "mailto:studio@modelerman.com",
    order_index: record.orderIndex ?? index,
    published: record.published ?? true,
});

export const fetchServices = async () => {
    if (!isSupabaseConfigured || !supabase) {
        return staticServices.map((item, index) => ({ ...item, orderIndex: index }));
    }

    const { data, error } = await supabase
        .from(TABLE_NAME)
        .select(
            "id, slug, category, card_title, card_summary, title, intro, focus_points, highlights, deliverables, process, tools, faq, cta_label, cta_url, order_index, published"
        )
        .order("order_index", { ascending: true });

    if (error) {
        console.error("Supabase fetchServices error", error);
        throw error;
    }

    return (data ?? []).map(mapRecord);
};

export const upsertService = async (payload) => {
    if (!isSupabaseConfigured || !supabase) {
        throw new Error(
            "Supabase yapılandırılmadan kayıt güncellenemez. Lütfen .env içinde REACT_APP_SUPABASE_URL ve REACT_APP_SUPABASE_ANON_KEY değerlerini tanımlayın."
        );
    }

    const { data, error } = await supabase.from(TABLE_NAME).upsert(toSupabaseRecord(payload, payload.orderIndex)).select().single();

    if (error) {
        console.error("Supabase upsertService error", error);
        throw error;
    }

    return mapRecord(data);
};

export const deleteService = async (id) => {
    if (!isSupabaseConfigured || !supabase) {
        throw new Error("Supabase yapılandırılmadan kayıt silinemez.");
    }

    const { error } = await supabase.from(TABLE_NAME).delete().eq("id", id);

    if (error) {
        console.error("Supabase deleteService error", error);
        throw error;
    }

    return true;
};

export const reorderServices = async (orderedIds) => {
    if (!isSupabaseConfigured || !supabase) {
        throw new Error("Supabase yapılandırılmadan sıralama güncellenemez.");
    }

    const updates = orderedIds.map((id, index) => ({ id, order_index: index }));
    const { error } = await supabase.from(TABLE_NAME).upsert(updates);

    if (error) {
        console.error("Supabase reorderServices error", error);
        throw error;
    }

    return true;
};

export const seedServices = async () => {
    if (!isSupabaseConfigured || !supabase) {
        throw new Error("Supabase yapılandırması yapılmadan veri yüklenemez.");
    }

    const payload = staticServices.map((item, index) => toSupabaseRecord({ ...item, orderIndex: index }, index));
    const { data, error } = await supabase.from(TABLE_NAME).upsert(payload, { onConflict: "id" }).select();

    if (error) {
        console.error("Supabase seedServices error", error);
        throw error;
    }

    return (data ?? payload).map(mapRecord).sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
};
